import type { ReactNode } from 'react'
import React from 'react'
import { View } from 'react-native'
import { tv } from 'tailwind-variants'

import { ConfigurableParameters } from '@flows-ui/components/quote/ConfigurableParameters'
import { CoverageBenefits } from '@flows-ui/components/quote/CoverageBenefits'
import { CoverageInformation } from '@flows-ui/components/quote/CoverageInformation'
import { CoveragePlans } from '@flows-ui/components/quote/CoveragePlans'
import { Extensions } from '@flows-ui/components/quote/Extensions'
import { Header } from '@flows-ui/components/quote/Header'
import { Loader } from '@flows-ui/components/quote/Loader'
import type { StepQuoteProps } from '@flows-ui/components/quote/StepQuote'
import { useQuoteLoader } from '@flows-ui/components/quote/hooks'
import { useQuoteStepViewedTracking } from '@flows-ui/components/quote/hooks/useQuoteStepViewedTracking'
import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { ProductGenerationData } from '@flows-ui/services/QuoteDataContext.types'
import type { CoverageInformationItem, ProductKey } from '@flows/index'

const styles = tv({
  slots: {
    container: 'mx-auto w-full flex-col gap-8',
    header: 'w-full self-center sm:max-w-[350px] md:max-w-[449px]',
    sections: 'w-full flex-col gap-10 self-center md:w-[649px]',
  },
})

export interface CoverageQuoteProps extends StepQuoteProps {
  renderRichContent?: (blocks: unknown[]) => ReactNode
}

interface SectionsProps extends CoverageQuoteProps {
  productGeneration: ProductGenerationData
}

const AddOns = (props: SectionsProps) =>
  props.productGeneration.extensions.length > 0 ? (
    <Extensions
      extensions={props.productGeneration.extensions}
      productKey={props.productGeneration.productKey}
      extraContent={props.extensionExtraContent}
      stepContentRenderer={props.stepContentRenderer}
      componentMap={props.componentMap}
    />
  ) : null

const ProductGenerationSections = (props: SectionsProps) => {
  const renderInfoContent = (item: CoverageInformationItem) =>
    item.webContent ? props.renderRichContent?.(item.webContent) : undefined

  return (
    <>
      <CoverageBenefits benefits={props.productGeneration.coverageBenefits ?? []} />
      <AddOns {...props} />
      <CoverageInformation
        items={props.productGeneration.coverageInformation ?? []}
        renderContent={renderInfoContent}
      />
    </>
  )
}

const QuoteSections = (props: CoverageQuoteProps) => {
  const { productGeneration } = useQuoteData()

  return (
    <>
      <CoveragePlans
        productKey={props.product}
        isCoveragePlanSelectorHidden={props.isCoveragePlanSelectorHidden}
        comparisonPresentation="accordion"
      />
      <ConfigurableParameters
        configurableParameters={props.configurableParameters}
        stepContentRenderer={props.stepContentRenderer}
        componentMap={props.componentMap}
        priceWatchProductKey={props.product}
      />
      {productGeneration ? (
        <ProductGenerationSections {...props} productGeneration={productGeneration} />
      ) : null}
    </>
  )
}

const toHeaderProduct = (
  productGeneration: ProductGenerationData | undefined,
  fallbackProductKey: ProductKey
) =>
  productGeneration
    ? { name: productGeneration.titleShort, key: productGeneration.productKey }
    : { name: '', key: fallbackProductKey }

const QuoteHeader = (props: CoverageQuoteProps) => {
  const { productGeneration } = useQuoteData()
  const product = toHeaderProduct(productGeneration, props.product)

  return (
    <Header
      productName={product.name}
      headerTitle={props.headerTitle}
      headerSubtitle={props.headerSubtitle}
      productKey={product.key}
      allowedPremiumIntervals={props.allowedPremiumIntervals}
    />
  )
}

export const CoverageQuote = (props: CoverageQuoteProps) => {
  const showLoader = useQuoteLoader(props.showPriceCalculationLoader)
  const { container, header, sections } = styles()

  useQuoteStepViewedTracking(props.product)

  if (showLoader) {
    return <Loader />
  }

  return (
    <View className={container()}>
      <View className={header()}>
        <QuoteHeader {...props} />
      </View>
      <View className={sections()}>
        <QuoteSections {...props} />
      </View>
    </View>
  )
}
